import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Tooltip from '../../../../components/molecules/Tooltip';
import { Table } from '../../../../components/widgets';
import TableActions from '../../../../components/widgets/TableActions';
import tableActionsCss from '../../../../components/widgets/TableActions.css';
import TableCellAutocomplete from '../../../../components/widgets/TableCellAutocomplete';
import TableCellTextInput from '../../../../components/widgets/TableCellTextInput';
import css from './PostbackFilters.css';
import { sortAutoCompleteOptions } from '../../../../utils/components/formUtils';
import TableCellMultiSelect from '../../../../components/widgets/TableCellMultiSelect';

export const POSTBACK_FILTERS_TYPES = {
    MULTI_SELECT: 'multi-select',
    NUMBER: 'number',
};

export const FILTERS_ACTIONS = {
    INCLUDE: 'include',
    EXCLUDE: 'exclude',
    EQUALS: 'equals',
    GREATER_THAN: 'greater_than',
    SMALLER_THAN: 'smaller_than',
};

export const ACTION_DISPLAY_NAMES = {
    [FILTERS_ACTIONS.INCLUDE]: 'Include',
    [FILTERS_ACTIONS.EXCLUDE]: 'Exclude',
    [FILTERS_ACTIONS.EQUALS]: 'Equals',
    [FILTERS_ACTIONS.GREATER_THAN]: 'Greater than',
    [FILTERS_ACTIONS.SMALLER_THAN]: 'Smaller than',
};

const multiSelectActions = [FILTERS_ACTIONS.INCLUDE, FILTERS_ACTIONS.EXCLUDE];

const textNumberActions = [
    FILTERS_ACTIONS.EQUALS,
    FILTERS_ACTIONS.GREATER_THAN,
    FILTERS_ACTIONS.SMALLER_THAN,
    FILTERS_ACTIONS.EXCLUDE,
];

const getActionOptions = filterType => {
    const actions = filterType === POSTBACK_FILTERS_TYPES.MULTI_SELECT ? multiSelectActions : textNumberActions;
    return actions.map(action => ({
        display_name: ACTION_DISPLAY_NAMES[action],
        name: action,
    }));
};

const PostbackFilters = ({ filters = [], availableFilters = [], onFiltersChange, disabled = false, errors = [] }) => {
    const [localFilters, setLocalFilters] = useState(filters);

    const filterMap = useMemo(() => {
        return availableFilters.reduce((map, filter) => {
            map[filter.name] = filter;
            return map;
        }, {});
    }, [availableFilters]);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleFieldChange = useCallback(
        (value, field, index) => {
            const updatedFilters = [...localFilters];
            updatedFilters[index] = {
                ...updatedFilters[index],
                [field]: value,
            };

            // Reset filter value and action when dimension changes
            if (field === 'filter') {
                // Get the filter definition to check its type
                const filterDef = filterMap[value];
                const isMultiSelect = filterDef?.type === POSTBACK_FILTERS_TYPES.MULTI_SELECT;
                updatedFilters[index].filterValue = isMultiSelect ? [] : '';

                // Get available actions for this filter (excluding already used ones)
                const actionOptions = getActionOptions(filterDef?.type);
                const usedActions = localFilters
                    .filter((f, idx) => f.filter === value && idx !== index)
                    .map(f => f.action);

                const availableActions = actionOptions.filter(action => !usedActions.includes(action.name));

                // Set default action to the first available action
                updatedFilters[index].action = availableActions.length > 0 ? availableActions[0].name : null;
            }

            setLocalFilters(updatedFilters);
            onFiltersChange(updatedFilters);
        },
        [localFilters, onFiltersChange, filterMap]
    );

    // Special handler for text input that only provides value
    const handleTextInputChange = useCallback(
        (value, row, field, index) => {
            handleFieldChange(value, field, index);
        },
        [handleFieldChange]
    );

    const handleAddFilter = useCallback(() => {
        const newFilter = {
            filter: null,
            action: null,
            filterValue: '', // Initialize as empty string (will be set to array for multi-select on filter selection)
        };
        const updatedFilters = [...localFilters, newFilter];
        setLocalFilters(updatedFilters);
        onFiltersChange(updatedFilters);
    }, [localFilters, onFiltersChange]);

    const handleRemoveFilter = useCallback(
        index => {
            const updatedFilters = localFilters.filter((_, i) => i !== index);
            setLocalFilters(updatedFilters);
            onFiltersChange(updatedFilters);
        },
        [localFilters, onFiltersChange]
    );

    // Get available filter options excluding those with all actions exhausted
    const getAvailableFilterOptions = useCallback(
        currentRowIndex => {
            return availableFilters
                .filter(filterDef => {
                    // Get all possible actions for this filter type
                    const possibleActions = getActionOptions(filterDef.type);

                    // Get all actions already used for this filter (excluding current row)
                    const usedActions = localFilters
                        .filter((f, idx) => f.filter === filterDef.name && idx !== currentRowIndex)
                        .map(f => f.action);

                    // If all possible actions are already used for this filter, exclude it
                    const allActionsExhausted = possibleActions.every(action => usedActions.includes(action.name));

                    return !allActionsExhausted;
                })
                .map(dim => ({
                    label: dim.displayName || dim.name,
                    value: dim.name,
                }));
        },
        [availableFilters, localFilters]
    );

    // Table metadata configuration
    const filterCell = {
        name: 'filter',
        displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.FILTER',
        headerAlign: 'left',
        headerWidth: 200,
        cellComponent: TableCellAutocomplete,
        cellProps: {
            options: getAvailableFilterOptions,
            placeholder: 'Select filter',
            onChange: (value, id, index) => handleFieldChange(value, 'filter', index),
        },
        cellValues: [
            {
                propName: 'value',
                transformation: (_, index) => {
                    const filterName = localFilters[index]?.filter;
                    if (!filterName) return null;
                    const filterDef = filterMap[filterName];
                    return filterDef ? { label: filterDef.displayName || filterDef.name, value: filterDef.name } : null;
                },
            },
            {
                propName: 'disabled',
                transformation: () => disabled,
            },
            {
                propName: 'error',
                transformation: (_, index) => (errors[index]?.filter ? ' ' : null),
            },
        ],
    };

    const getAvailableFilterActionOptions = useCallback(
        currentRowIndex => {
            const filter = localFilters[currentRowIndex];
            if (!filter?.filter) return [];

            const filterDef = filterMap[filter.filter];
            const actionOptions = getActionOptions(filterDef?.type);

            // Get all actions already used for this filter (excluding current row)
            const usedActions = localFilters
                .filter((f, idx) => f.filter === filter.filter && idx !== currentRowIndex)
                .map(f => f.action);

            // Filter out already used actions
            return actionOptions
                .filter(action => !usedActions.includes(action.name))
                .map(action => ({
                    label: action.display_name,
                    value: action.name,
                }));
        },
        [localFilters, filterMap]
    );

    const actionCell = {
        name: 'action',
        displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.ACTION',
        headerAlign: 'left',
        headerWidth: 155,
        cellComponent: TableCellAutocomplete,
        cellProps: {
            options: getAvailableFilterActionOptions,
            placeholder: 'Select action',
            onChange: (value, id, index) => handleFieldChange(value, 'action', index),
        },
        cellValues: [
            {
                propName: 'value',
                transformation: (_, index) => {
                    const filterName = localFilters[index]?.filter;
                    if (!filterName) return null;
                    const filterDef = filterMap[filterName];
                    const actionOptions = getActionOptions(filterDef?.type);
                    const action = localFilters[index]?.action;
                    const actionOption = actionOptions.find(opt => opt.name === action);
                    return actionOption ? { label: actionOption.display_name, value: actionOption.name } : null;
                },
            },
            {
                propName: 'disabled',
                transformation: (_, index) => {
                    const hasFilter = localFilters[index]?.filter;
                    return !hasFilter || disabled;
                },
            },
        ],
    };

    const FilterValueCell = useCallback(
        ({ value, rowIndex, disabled: cellDisabled, onChange: tableOnChange, error }) => {
            const filterName = localFilters[rowIndex]?.filter;
            const filterDef = filterMap[filterName];
            const isMultiSelect = filterDef?.type === POSTBACK_FILTERS_TYPES.MULTI_SELECT;

            const handleMultiSelectChange = tags => {
                // Extract values from tag objects
                const values = tags.map(tag => tag.value || tag.label || tag);
                handleFieldChange(values, 'filterValue', rowIndex);
            };

            if (isMultiSelect) {
                // Convert stored values (array of strings) to tag objects
                const tags = Array.isArray(value) ? value.map(val => ({ label: val, value: val })) : [];
                const options =
                    filterDef?.options
                        ?.map(option => ({
                            value: option.name,
                            label: option.displayName,
                        }))
                        .sort(sortAutoCompleteOptions) || [];

                return (
                    <TableCellMultiSelect
                        creatable={options.length === 0}
                        containerClass={css.multiSelect}
                        value={tags}
                        options={options}
                        onChange={handleMultiSelectChange}
                        disabled={cellDisabled || !filterDef}
                        placeholder={
                            options.length === 0 ? 'Type a value and press Enter' : 'Type & press Enter, or select...'
                        }
                        error={error}
                        menuPlacement="auto"
                    />
                );
            }

            return (
                <TableCellTextInput
                    placeholder={filterDef?.type ? `Add ${filterDef?.type} Value` : ''}
                    type={filterDef?.type}
                    value={value ?? ''}
                    rowIndex={rowIndex}
                    disabled={cellDisabled || !filterDef}
                    onChange={tableOnChange}
                    error={error}
                />
            );
        },
        [localFilters, filterMap, handleFieldChange]
    );

    const filterValueCell = {
        name: 'filterValue',
        displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.FILTER_VALUES',
        headerAlign: 'left',
        headerWidth: 370,
        cellComponent: FilterValueCell,
        cellProps: {},
        cellValues: [
            {
                propName: 'value',
                transformation: (_, index) => localFilters[index]?.filterValue ?? '',
            },
            {
                propName: 'disabled',
                transformation: (_, index) => !localFilters[index]?.filter || disabled,
            },
            {
                propName: 'error',
                transformation: (_, index) => (errors[index]?.filterValue ? ' ' : null),
            },
        ],
    };

    const tableActionsCell = {
        name: 'actions',
        displayName: '',
        headerAlign: 'center',
        cellComponent: TableActions,
        cellProps: {
            onAdd: disabled ? null : handleAddFilter,
            onDelete: disabled ? null : handleRemoveFilter,
            addClassName: index => {
                if (!(index === localFilters.length - 1)) {
                    return tableActionsCss.hidden;
                }

                const currentFilter = localFilters[index];
                const filterDef = filterMap[currentFilter?.filter];

                // Check if filter value is empty based on filter type
                const hasEmptyValue =
                    filterDef?.type === POSTBACK_FILTERS_TYPES.MULTI_SELECT
                        ? !Array.isArray(currentFilter?.filterValue) || currentFilter.filterValue.length === 0
                        : currentFilter?.filterValue === null ||
                          currentFilter?.filterValue === undefined ||
                          currentFilter?.filterValue === '';

                // Hide add button if not last row OR if filter/value is empty
                return !currentFilter?.filter || hasEmptyValue ? tableActionsCss.disabled : '';
            },
        },
    };

    const tableMetadata = [filterCell, actionCell, filterValueCell, tableActionsCell];

    // Check if we have available filters to enable adding filters
    const canAddFilters = availableFilters && availableFilters.length > 0;
    const hasFilters = localFilters && localFilters.length > 0;

    return (
        <div className={css.postbackFiltersContainer}>
            <div className={css.filterHeader}>
                <Translate id="STATIC.FILTERS" />
            </div>
            <div className={css.subHeader}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.FILTERS_DESCRIPTION" />
            </div>

            {!hasFilters ? (
                <Tooltip
                    titleTranslationKey="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.DISABLED_POSTBACK_FILTERS_MESSAGE"
                    disabled={canAddFilters && !disabled}
                >
                    <div
                        onClick={canAddFilters && !disabled ? handleAddFilter : undefined}
                        className={`${css.addFilterButton} ${!canAddFilters || disabled ? css.disabled : ''}`}
                    >
                        <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.ADD_FILTER" />
                    </div>
                </Tooltip>
            ) : (
                <Table
                    data={localFilters}
                    metadata={tableMetadata}
                    zebraTable={false}
                    rowHoverEnabled
                    onToggleSingleRow={handleTextInputChange}
                    tableClass={css.filtersTable}
                />
            )}
        </div>
    );
};

PostbackFilters.propTypes = {
    filters: PropTypes.arrayOf(
        PropTypes.shape({
            filter: PropTypes.string,
            action: PropTypes.oneOf(Object.values(FILTERS_ACTIONS)),
            filterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.string)]),
        })
    ),
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
    onFiltersChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(
        PropTypes.shape({
            filter: PropTypes.bool,
            filterValue: PropTypes.bool,
        })
    ),
};

export default PostbackFilters;
